import clsx from 'clsx';
import React from 'react';

import { Column, Grid } from '@/components/global/Grid';
import { SITE_URL, STORYBLOK_IMAGE_QUALITY } from '@/lib/constants';
import { useRandomNumber } from '@/lib/hooks/randomNumber';

import styles from './Hero.module.scss';
import HeroImage from './HeroImage';

const chooseRandom = (list, keyNumber) => {
  if (!list || list?.length === 0) return;
  const ran = keyNumber;
  return list[ran];
};

type BackgroundImage = {
  filename: string;
  alt: string;
};

interface IHeroProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  footerMargin?: string;
  topBorder?: string;
  bottomBorder?: string;
  topGradient?: boolean;
  bottomGradient?: boolean;
  backgroundDesktop?: BackgroundImage;
  backgroundTablet?: BackgroundImage;
  backgroundMobile?: BackgroundImage;
  backgroundColorPreset?: string;
  variant?: string;
  backgroundColor?: string;
  html?: string;
  css?: string;
  sizeImage?: string;
  eager?: boolean;
  images?: any;
}

const Hero: React.FC<IHeroProps> = ({
  title,
  subtitle,
  footer,
  footerMargin,
  topBorder,
  bottomBorder,
  topGradient = false,
  bottomGradient = false,
  backgroundColor,
  backgroundColorPreset,
  html,
  css,
  sizeImage,
  variant,
  eager = true,
  backgroundDesktop,
  backgroundTablet,
  backgroundMobile,
  images = []
}) => {
  const keyNumber = useRandomNumber(images.length);
  const randomImage = chooseRandom(images, keyNumber);
  const backgroundColorStyle = backgroundColor ? backgroundColor : backgroundColorPreset;

  const host = `${SITE_URL}cdn/`;
  const imageIsSVG = (image) => image.endsWith('.svg');

  const imagePath = (image, size) =>
    imageIsSVG(image)
      ? image.replace('https://a.storyblok.com/', host)
      : image.replace('https://a.storyblok.com/', host) + `/m/${size}x0/filters:quality(100)`;

  return (
    <div
      style={{ backgroundColor: backgroundColorStyle ? backgroundColorStyle : 'transparent' }}
      className={clsx(styles.container, styles[variant])}
    >
      <div className={clsx(styles.heroLayer)}>
        <div className={clsx(styles.hero)}>
          {topBorder && (
            <Grid rowPadding="0">
              <Column sm={12} md={12} lg={12}>
                <div className={clsx(topBorder && styles[`border-${topBorder}`])}></div>
              </Column>
            </Grid>
          )}
          <div className={styles.heroContent}>
            {title && <div className={styles.title}>{title}</div>}
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            {html && (
              <div
                dangerouslySetInnerHTML={{
                  __html: `${html}`
                }}
              />
            )}
            {css && (
              <style
                dangerouslySetInnerHTML={{
                  __html: `${css}`
                }}
              />
            )}
          </div>
          <Grid rowPadding="0">
            <Column sm={12} md={12} lg={12}>
              <div className={clsx(bottomBorder && styles[`border-${bottomBorder}`])}></div>
              {footer && <div className={styles.footer}>{footer}</div>}
            </Column>
          </Grid>
        </div>
      </div>

      <div
        className={clsx(styles.imageLayer, {
          [styles.bottomGradient]: bottomGradient,
          [styles.topGradient]: topGradient,
          [styles.imageLayerFullWidth]: sizeImage === 'full-width',
          [styles.imageLayerFill]: sizeImage === 'fill-hero'
        })}
      >
        {randomImage && <HeroImage blok={randomImage} eager={eager} />}
        {backgroundDesktop?.filename && (
          <picture>
            <source
              srcSet={imagePath(backgroundDesktop?.filename, 2500)}
              media="(min-width: 1024px)"
            />
            {backgroundTablet?.filename && (
              <source
                srcSet={imagePath(backgroundTablet?.filename, 1800)}
                media="(max-width: 1023px)"
              />
            )}
            {backgroundMobile?.filename && (
              <source
                srcSet={imagePath(backgroundMobile?.filename, 1300)}
                media="(max-width: 767px)"
              />
            )}
            <img
              src={imagePath(backgroundDesktop?.filename, 1300)}
              alt={backgroundDesktop?.alt}
              loading={eager ? 'eager' : 'lazy'}
              /*
              // @ts-ignore */
              fetchPriority={eager ? 'high' : 'low'}
            />
          </picture>
        )}
      </div>
    </div>
  );
};

export default Hero;
